import { useEffect, useState } from "react";
import {
    BookOpen,
    Boxes,
    Building2,
    CreditCard,
    Library,
    LogOut,
    Menu,
    ShieldCheck,
    Sword,
    X,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { platformAccess } from "../lib/api/platform";
import { AUTH_CHANGED_EVENTS, clearToken, isAuthed } from "../lib/auth";

const APP_LINKS = [
    { to: "/servers", label: "Servers", icon: Boxes },
    { to: "/catalogue", label: "Catalogue", icon: Library },
    { to: "/orgs", label: "Organisations", icon: Building2 },
    { to: "/skills", label: "Skills", icon: BookOpen },
    { to: "/capability-bundles", label: "Capabilities", icon: ShieldCheck },
    { to: "/connections", label: "Connections", icon: Boxes },
    { to: "/billing", label: "Billing", icon: CreditCard },
    { to: "/settings/security", label: "Security", icon: ShieldCheck },
    { to: '/harness', label: 'AI clients', icon: Boxes },
] as const;

const PUBLIC_LINKS = [
    { to: "/catalogue", label: "Catalogue" },
    { to: "/docs/quickstart", label: "Docs" },
    { to: "/pricing", label: "Pricing" },
] as const;

export function Brand({ compact = false }: { compact?: boolean }) {
    return (
        <span className="inline-flex items-center gap-2.5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-pugio-text text-white shadow-xs">
                <Sword size={16} strokeWidth={1.8} aria-hidden="true" />
            </span>
            {!compact && (
                <span className="text-[17px] font-semibold text-pugio-text">Pugio</span>
            )}
        </span>
    );
}

function isActive(pathname: string, to: string): boolean {
    if (to === "/servers") return pathname === to || pathname.startsWith("/servers/");
    return pathname === to || pathname.startsWith(`${to}/`);
}

export function Navbar() {
    const [authed, setAuthed] = useState<boolean>(() => isAuthed());
    const [authRevision, setAuthRevision] = useState(0);
    const [platformAdmin, setPlatformAdmin] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();

    useEffect(() => {
        const update = () => { setAuthed(isAuthed()); setAuthRevision(value => value + 1); };
        for (const event of AUTH_CHANGED_EVENTS) window.addEventListener(event, update);
        window.addEventListener("storage", update);
        return () => {
            for (const event of AUTH_CHANGED_EVENTS) window.removeEventListener(event, update);
            window.removeEventListener("storage", update);
        };
    }, []);

    useEffect(() => setOpen(false), [pathname]);
    useEffect(() => {
        let active = true;
        setPlatformAdmin(false);
        if (authed) void platformAccess().then(value => { if (active) setPlatformAdmin(value.eligible); }).catch(() => {});
        return () => { active = false; };
    }, [authed, pathname, authRevision]);

    function logout() {
        clearToken();
        navigate("/", { replace: true });
    }

    const onAuthPage = pathname === "/login" || pathname === "/signup";
    const links = authed ? [APP_LINKS[0], ...(platformAdmin ? [{ to: "/platform", label: "Platform", icon: ShieldCheck }] : []), ...APP_LINKS.slice(1)] : PUBLIC_LINKS;

    return (
        <header className="sticky top-0 z-40 border-b border-pugio-border bg-white/95 backdrop-blur">
            <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-6 lg:px-8">
                <Link to={authed ? "/servers" : "/"} aria-label="Pugio home" className="shrink-0">
                    <Brand />
                </Link>

                {!onAuthPage && (
                    <nav className="hidden min-w-0 flex-1 items-center gap-1 overflow-x-auto xl:flex" aria-label="Primary navigation">
                        {links.map(({ to, label, ...rest }) => {
                            const ActiveIcon = "icon" in rest ? rest.icon : null;
                            const active = isActive(pathname, to);
                            return (
                                <Link
                                    key={to}
                                    to={to}
                                    className={`inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium transition-colors ${
                                        active
                                            ? "bg-emerald-50 text-pugio-accent"
                                            : "text-pugio-muted hover:bg-pugio-bg hover:text-pugio-text"
                                    }`}
                                >
                                    {ActiveIcon && <ActiveIcon size={15} strokeWidth={1.8} aria-hidden="true" />}
                                    {label}
                                </Link>
                            );
                        })}
                    </nav>
                )}

                <div className="ml-auto hidden items-center gap-2 xl:flex">
                    {authed ? (
                        <button onClick={logout} className="btn-ghost" type="button">
                            <LogOut size={15} aria-hidden="true" />
                            Sign out
                        </button>
                    ) : onAuthPage ? null : (
                        <>
                            <Link to="/login" className="btn-ghost">Log in</Link>
                            <Link to="/signup" className="btn-primary">Start free</Link>
                        </>
                    )}
                </div>

                {!onAuthPage && (
                    <button
                        type="button"
                        className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-md border border-pugio-border bg-white text-pugio-text xl:hidden"
                        onClick={() => setOpen((value) => !value)}
                        aria-label={open ? "Close navigation" : "Open navigation"}
                        aria-expanded={open}
                    >
                        {open ? <X size={19} /> : <Menu size={19} />}
                    </button>
                )}
            </div>

            {open && !onAuthPage && (
                <div className="border-t border-pugio-border bg-white px-4 py-3 shadow-soft xl:hidden">
                    <nav className="mx-auto grid max-w-7xl gap-1" aria-label="Mobile navigation">
                        {links.map(({ to, label, ...rest }) => {
                            const ActiveIcon = "icon" in rest ? rest.icon : null;
                            const active = isActive(pathname, to);
                            return (
                                <Link
                                    key={to}
                                    to={to}
                                    className={`flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-medium ${
                                        active ? "bg-emerald-50 text-pugio-accent" : "text-pugio-muted"
                                    }`}
                                >
                                    {ActiveIcon && <ActiveIcon size={17} aria-hidden="true" />}
                                    {label}
                                </Link>
                            );
                        })}
                        <div className="mt-2 border-t border-pugio-border pt-3">
                            {authed ? (
                                <button type="button" onClick={logout} className="btn-ghost w-full justify-start">
                                    <LogOut size={17} aria-hidden="true" />
                                    Sign out
                                </button>
                            ) : (
                                <div className="grid grid-cols-2 gap-2">
                                    <Link to="/login" className="btn-ghost">Log in</Link>
                                    <Link to="/signup" className="btn-primary">Start free</Link>
                                </div>
                            )}
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
}
